import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Message,
  Progress,
  Segment,
} from 'semantic-ui-react';
import {
  API,
  isErrorShown,
  showError,
  showInfo,
  showSuccess,
  showWarning,
} from '../helpers';
import globalStyles from '../styles/globalStyles';

// 进行中的阶段，需要轮询进度
const activeStages = [
  'checking',
  'downloading',
  'verifying',
  'backing_up',
  'replacing',
  'restarting',
];

const defaultConfig = {
  enabled: false,
  download_base_url: '',
  check_interval: 3600,
  auto_download: false,
  auto_apply: false,
  channel: 'stable',
  oss_access_key: '',
  oss_access_secret: '',
};

const defaultStatus = {
  current_version: '',
  target_version: '',
  current_sha256: '',
  target_sha256: '',
  last_check: 0,
  last_check_error: '',
  remote_config_error: '',
  has_update: false,
};

const defaultTask = {
  stage: 'idle',
  percent: 0,
  stage_percent: 0,
  message: '',
  downloaded: 0,
  total_size: 0,
  error: '',
};

const AutoUpdateSetting = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(defaultConfig);
  const [originConfig, setOriginConfig] = useState(defaultConfig);
  const [status, setStatus] = useState(defaultStatus);
  const [task, setTask] = useState(defaultTask);
  const [hasUpdate, setHasUpdate] = useState(false);
  const pollRef = useRef(null);
  const pollInFlightRef = useRef(false);
  // 上传新版本相关
  const [uploadVersion, setUploadVersion] = useState('');
  const [uploadPlatform, setUploadPlatform] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const showCaughtError = (error, fallbackKey) => {
    if (!isErrorShown(error)) {
      showError(error || t(fallbackKey));
    }
  };

  const getResponseBody = (res, fallbackKey, silent = false) => {
    if (!res || !res.data || typeof res.data !== 'object') {
      if (!silent) {
        showError(t(fallbackKey));
      }
      return null;
    }
    return res.data;
  };

  const loadConfig = async ({ silent = false } = {}) => {
    try {
      const res = await API.get('/api/autoupdate/config');
      const body = getResponseBody(res, 'setting.autoupdate.messages.load_config_failed', silent);
      if (!body) return false;
      const { success, message, data } = body;
      if (!success) {
        if (!silent) {
          showError(message || t('setting.autoupdate.messages.load_config_failed'));
        }
        return false;
      }
      if (!data || typeof data !== 'object') {
        if (!silent) {
          showError(t('setting.autoupdate.messages.load_config_failed'));
        }
        return false;
      }
      const nextConfig = { ...defaultConfig, ...data };
      setConfig(nextConfig);
      setOriginConfig(nextConfig);
      return true;
    } catch (error) {
      if (!silent) {
        showCaughtError(error, 'setting.autoupdate.messages.load_config_failed');
      }
      return false;
    } finally {
      // Reserved for consistent async cleanup.
    }
  };

  const loadStatus = async ({ silent = false } = {}) => {
    try {
      const res = await API.get('/api/autoupdate/status');
      const body = getResponseBody(res, 'setting.autoupdate.messages.load_status_failed', silent);
      if (!body) return false;
      const { success, message, data } = body;
      if (!success) {
        if (!silent) {
          showError(message || t('setting.autoupdate.messages.load_status_failed'));
        }
        return false;
      }
      if (!data || typeof data !== 'object') {
        if (!silent) {
          showError(t('setting.autoupdate.messages.load_status_failed'));
        }
        return false;
      }
      setStatus({
        ...defaultStatus,
        current_version: data.current_version || '',
        target_version: data.target_version || '',
        current_sha256: data.current_sha256 || '',
        target_sha256: data.target_sha256 || '',
        last_check: data.last_check || 0,
        last_check_error: data.last_check_error || '',
        remote_config_error: data.remote_config_error || '',
        has_update: Boolean(data.has_update),
      });
      setHasUpdate(Boolean(data.has_update));
      if (data.task && typeof data.task === 'object') {
        setTask({ ...defaultTask, ...data.task });
      }
      return true;
    } catch (error) {
      if (!silent) {
        showCaughtError(error, 'setting.autoupdate.messages.load_status_failed');
      }
      return false;
    } finally {
      // Reserved for consistent async cleanup.
    }
  };

  useEffect(() => {
    loadConfig();
    loadStatus();
  }, []);

  // 轮询进度（仅任务进行中时）
  useEffect(() => {
    const stopPolling = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const pollProgress = async () => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
        const res = await API.get('/api/autoupdate/progress');
        const body = getResponseBody(res, 'setting.autoupdate.messages.poll_failed');
        if (!body) {
          stopPolling();
          return;
        }
        const { success, message, data } = body;
        if (!success) {
          showError(message || t('setting.autoupdate.messages.poll_failed'));
          stopPolling();
          return;
        }
        if (!data || typeof data !== 'object') {
          showError(t('setting.autoupdate.messages.poll_failed'));
          stopPolling();
          return;
        }
        setTask({ ...defaultTask, ...data });
        if (['done', 'failed', 'idle', 'pending'].includes(data.stage)) {
          stopPolling();
          await loadStatus();
        }
      } catch (error) {
        showCaughtError(error, 'setting.autoupdate.messages.poll_failed');
        stopPolling();
      } finally {
        pollInFlightRef.current = false;
      }
    };

    if (activeStages.includes(task.stage)) {
      if (!pollRef.current) {
        pollRef.current = setInterval(pollProgress, 1000);
      }
    } else {
      stopPolling();
    }
    return stopPolling;
  }, [task.stage]);

  const handleInputChange = (e, { name, value, checked }) => {
    let v = name === 'enabled' || name === 'auto_download' || name === 'auto_apply' ? checked : value;
    if (name === 'check_interval') {
      v = parseInt(value, 10) || 0;
    }
    setConfig((cfg) => ({ ...cfg, [name]: v }));
  };

  // 保存更新设置（开关类，即时生效）
  const submitUpdateSettings = async () => {
    setLoading(true);
    try {
      const res = await API.put('/api/autoupdate/config', config);
      const body = getResponseBody(res, 'setting.autoupdate.messages.save_failed');
      if (!body) return;
      const { success, message } = body;
      if (!success) {
        showError(message || t('setting.autoupdate.messages.save_failed'));
        return;
      }
      showSuccess(message || t('setting.autoupdate.messages.save_success'));
      setOriginConfig(config);
    } catch (error) {
      showCaughtError(error, 'setting.autoupdate.messages.save_failed');
    } finally {
      setLoading(false);
    }
  };

  // 保存 OSS 配置（凭证 + 下载地址，写回远程用）
  const submitOSSConfig = async () => {
    setLoading(true);
    try {
      const res = await API.put('/api/autoupdate/config', config);
      const body = getResponseBody(res, 'setting.autoupdate.messages.save_failed');
      if (!body) return;
      const { success, message } = body;
      if (!success) {
        showError(message || t('setting.autoupdate.messages.save_failed'));
        return;
      }
      showSuccess(message || t('setting.autoupdate.messages.oss_save_success'));
      setOriginConfig(config);
    } catch (error) {
      showCaughtError(error, 'setting.autoupdate.messages.save_failed');
    } finally {
      setLoading(false);
    }
  };

  const shortHash = (hash) => (hash ? hash.slice(0, 12) : '-');

  const handleCheck = async () => {
    try {
      const res = await API.post('/api/autoupdate/check');
      const body = getResponseBody(res, 'setting.autoupdate.messages.check_failed');
      if (!body) return;
      const { success, message, data, no_update_reason: responseNoUpdateReason } = body;
      if (!success) {
        showError(message || t('setting.autoupdate.messages.check_failed'));
        return;
      }
      if (!data || typeof data !== 'object') {
        showError(t('setting.autoupdate.messages.check_failed'));
        return;
      }
      setHasUpdate(Boolean(data.has_update));
      setTask((currentTask) => ({ ...currentTask, stage: 'idle' }));
      if (data.has_update) {
        showSuccess(t('setting.autoupdate.messages.found_update', {
          package: shortHash(data.latest_sha256) !== '-' ? shortHash(data.latest_sha256) : (data.latest_version || '-'),
        }));
      } else {
        const noUpdateReason = data.no_update_reason || responseNoUpdateReason;
        showInfo(noUpdateReason
          ? t(`setting.autoupdate.no_update_reasons.${noUpdateReason}`, { defaultValue: noUpdateReason })
          : t('setting.autoupdate.messages.no_update'));
      }
      await loadStatus();
    } catch (error) {
      showCaughtError(error, 'setting.autoupdate.messages.check_failed');
    } finally {
      // No local loading state is held for manual checks.
    }
  };

  const handleApply = async () => {
    if (!window.confirm(t('setting.autoupdate.messages.apply_confirm'))) {
      return;
    }
    try {
      const res = await API.post('/api/autoupdate/apply');
      const body = getResponseBody(res, 'setting.autoupdate.messages.apply_failed');
      if (!body) return;
      const { success, message } = body;
      if (!success) {
        showError(message || t('setting.autoupdate.messages.apply_failed'));
        return;
      }
      showSuccess(t('setting.autoupdate.messages.apply_started'));
      setTask((currentTask) => ({
        ...currentTask,
        stage: 'checking',
        percent: 0,
        stage_percent: 0,
        message: t('setting.autoupdate.messages.apply_started'),
        error: '',
      }));
      window.setTimeout(() => {
        loadStatus();
      }, 500);
    } catch (error) {
      showCaughtError(error, 'setting.autoupdate.messages.apply_failed');
    } finally {
      // Progress polling owns subsequent task state.
    }
  };

  const handleCancel = async () => {
    try {
      const res = await API.post('/api/autoupdate/cancel');
      const body = getResponseBody(res, 'setting.autoupdate.messages.cancel_failed');
      if (!body) return;
      const { success, message } = body;
      if (!success) {
        showError(message || t('setting.autoupdate.messages.cancel_failed'));
        return;
      }
      showSuccess(t('setting.autoupdate.messages.cancel_success'));
      setTask(defaultTask);
      setHasUpdate(false);
    } catch (error) {
      showCaughtError(error, 'setting.autoupdate.messages.cancel_failed');
    } finally {
      // No local loading state is held for cancellation.
    }
  };

  const handleUpload = async () => {
    const fileInput = document.getElementById('update-file-input');
    if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
      showError(t('setting.autoupdate.messages.select_file'));
      return;
    }
    const file = fileInput.files[0];
    const formData = new FormData();
    formData.append('file', file);
    if (uploadVersion) {
      formData.append('version', uploadVersion);
    }
    if (uploadPlatform) {
      formData.append('platform', uploadPlatform);
    }
    setUploadResult(null);
    setUploading(true);
    try {
      const res = await API.post('/api/autoupdate/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const body = getResponseBody(res, 'setting.autoupdate.messages.upload_failed');
      if (!body) return;
      const { success, message, data, warning, matches_current: matchesCurrent } = body;
      if (!success) {
        showError(message || t('setting.autoupdate.messages.upload_failed'));
        return;
      }
      const result = data && typeof data === 'object' ? data : {};
      const uploadWarning = warning || result.warning;
      const isCurrentPackage = Boolean(matchesCurrent ?? result.matches_current);
      setUploadResult({ ...result, matches_current: isCurrentPackage, warning: uploadWarning || '' });
      setUploadVersion('');
      fileInput.value = '';
      showSuccess(t('setting.autoupdate.messages.upload_success'));
      if (isCurrentPackage) {
        showWarning(t('setting.autoupdate.messages.upload_matches_current'));
      }
      if (uploadWarning) {
        showWarning(t('setting.autoupdate.messages.upload_warning', { warning: uploadWarning }));
      }

      const [configLoaded, statusLoaded] = await Promise.all([
        loadConfig({ silent: true }),
        loadStatus({ silent: true }),
      ]);
      if (!configLoaded || !statusLoaded) {
        showWarning(t('setting.autoupdate.messages.upload_refresh_failed'));
      }
    } catch (error) {
      showCaughtError(error, 'setting.autoupdate.messages.upload_failed');
    } finally {
      setUploading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let v = bytes;
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024;
      i++;
    }
    return v.toFixed(1) + ' ' + units[i];
  };

  const isActive = activeStages.includes(task.stage);
  const isFailed = task.stage === 'failed';
  const isDone = task.stage === 'done';
  const progressColor = isFailed ? 'red' : isDone ? 'green' : 'blue';

  return (
    <div className='settings-setting-view settings-autoupdate'>
      <Grid columns={1}>
        <Grid.Column>
          <Form loading={loading} className='settings-form-stack'>
            <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: '#18181b', marginBottom: 16 }}>
              {t('setting.autoupdate.title')}
            </Header>

            {/* 状态区 */}
            <Segment className='settings-status-panel'>
              <Grid columns={4} stackable>
                <Grid.Column>
                  <strong>{t('setting.autoupdate.current_version')}：</strong>
                  <span style={{ marginLeft: 8 }}>{status.current_version || '-'}</span>
                </Grid.Column>
                <Grid.Column>
                  <strong>{t('setting.autoupdate.current_package')}：</strong>
                  <span style={{ marginLeft: 8 }} title={status.current_sha256 || undefined}>
                    {shortHash(status.current_sha256)}
                  </span>
                </Grid.Column>
                <Grid.Column>
                  <strong>{t('setting.autoupdate.target_package')}：</strong>
                  <span style={{ marginLeft: 8 }} title={status.target_sha256 || undefined}>
                    {shortHash(status.target_sha256)}
                  </span>
                </Grid.Column>
                <Grid.Column>
                  <strong>{t('setting.autoupdate.last_check')}：</strong>
                  <span style={{ marginLeft: 8 }}>
                    {status.last_check > 0
                      ? new Date(status.last_check * 1000).toLocaleString()
                      : '-'}
                  </span>
                </Grid.Column>
              </Grid>
            </Segment>

            {status.last_check_error && (
              <Message warning size='small'>
                <Message.Header>{t('setting.autoupdate.last_check_error')}</Message.Header>
                <p>{status.last_check_error}</p>
              </Message>
            )}
            {status.remote_config_error && (
              <Message negative size='small'>
                <Message.Header>{t('setting.autoupdate.remote_config_error')}</Message.Header>
                <p>{status.remote_config_error}</p>
              </Message>
            )}

            {/* 进度区 */}
            {(isActive || isFailed || isDone || task.message) && (
              <Segment className='settings-progress-panel'>
                <Progress
                  percent={task.percent || 0}
                  color={progressColor}
                  active={isActive}
                  success={isDone}
                  error={isFailed}
                  progress='percent'
                  label={t(`setting.autoupdate.stage.${task.stage}`, { defaultValue: task.stage })}
                />
                {task.message && (
                  <p style={{ color: 'gray', fontSize: '12px', marginTop: 2 }}>{task.message}</p>
                )}
                {task.stage === 'downloading' && task.total_size > 0 && (
                  <p style={{ color: 'gray', fontSize: '12px', marginTop: 4 }}>
                    {formatBytes(task.downloaded)} / {formatBytes(task.total_size)}
                  </p>
                )}
                {isFailed && task.error && (
                  <Message negative size='small'>
                    <Message.Header>{t('setting.autoupdate.messages.failed')}</Message.Header>
                    <p>{task.error}</p>
                  </Message>
                )}
                {isDone && (
                  <Message success size='small'>
                    <p>{t('setting.autoupdate.messages.done')}</p>
                  </Message>
                )}
                {isActive && (
                  <Button size='small' onClick={handleCancel}>
                    {t('setting.autoupdate.buttons.cancel')}
                  </Button>
                )}
              </Segment>
            )}

            {/* 操作按钮 */}
            <Form.Group>
              <Form.Button style={globalStyles.primaryButton} type='button' onClick={handleCheck}>
                {t('setting.autoupdate.buttons.check')}
              </Form.Button>
              {(hasUpdate || task.stage === 'pending') && (
                <Button color='green' type='button' onClick={handleApply}>
                  {t('setting.autoupdate.buttons.apply')}
                </Button>
              )}
              {isFailed && (
                <Button color='orange' type='button' onClick={handleApply}>
                  {t('setting.autoupdate.buttons.retry')}
                </Button>
              )}
            </Form.Group>

            <Divider />

            {/* 模块一：更新设置 */}
            <Header as='h4' style={{ fontSize: 14, fontWeight: 700, color: '#18181b', marginBottom: 12 }}>
              {t('setting.autoupdate.update_settings_title')}
            </Header>
            <Form.Group inline>
              <Form.Checkbox
                label={t('setting.autoupdate.enable')}
                name='enabled'
                checked={config.enabled}
                onChange={handleInputChange}
              />
              <Form.Checkbox
                label={t('setting.autoupdate.auto_download')}
                name='auto_download'
                checked={config.auto_download}
                onChange={handleInputChange}
              />
              <Form.Checkbox
                label={t('setting.autoupdate.auto_apply')}
                name='auto_apply'
                checked={config.auto_apply}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Input
                label={t('setting.autoupdate.check_interval')}
                name='check_interval'
                type='number'
                min='60'
                step='1'
                onChange={handleInputChange}
                value={config.check_interval}
                placeholder='3600'
              />
            </Form.Group>
            <p style={{ color: 'gray', fontSize: '12px' }}>
              {t('setting.autoupdate.check_interval_hint')}
            </p>
            <p style={{ color: 'gray', fontSize: '12px' }}>
              {t('setting.autoupdate.update_settings_hint')}
            </p>
            <Form.Button style={globalStyles.primaryButton} type='button' onClick={submitUpdateSettings}>
              {t('setting.autoupdate.buttons.save_update_settings')}
            </Form.Button>

            <Divider />

            {/* 模块二：OSS 配置 */}
            <Header as='h4' style={{ fontSize: 14, fontWeight: 700, color: '#18181b', marginBottom: 12 }}>
              {t('setting.autoupdate.oss_config_title')}
            </Header>
            <Form.Group widths='equal'>
              <Form.Input
                label={t('setting.autoupdate.download_base_url')}
                name='download_base_url'
                placeholder={t('setting.autoupdate.download_base_url_placeholder')}
                onChange={handleInputChange}
                value={config.download_base_url}
                autoComplete='new-password'
              />
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Input
                label={t('setting.autoupdate.oss_access_key')}
                name='oss_access_key'
                placeholder={t('setting.autoupdate.oss_access_key_placeholder')}
                onChange={handleInputChange}
                value={config.oss_access_key}
                autoComplete='new-password'
              />
              <Form.Input
                label={t('setting.autoupdate.oss_access_secret')}
                name='oss_access_secret'
                type='password'
                placeholder={t('setting.autoupdate.oss_access_secret_placeholder')}
                onChange={handleInputChange}
                value={config.oss_access_secret}
                autoComplete='new-password'
              />
            </Form.Group>
            <p style={{ color: 'gray', fontSize: '12px' }}>
              {t('setting.autoupdate.oss_config_hint')}
            </p>
            <Form.Button style={globalStyles.primaryButton} type='button' onClick={submitOSSConfig}>
              {t('setting.autoupdate.buttons.save_oss_config')}
            </Form.Button>

            <Divider />
            <Header as='h4' style={{ fontSize: 14, fontWeight: 700, color: '#18181b', marginBottom: 12 }}>
              {t('setting.autoupdate.upload_title')}
            </Header>
            <p style={{ color: 'gray', fontSize: '12px', marginBottom: 12 }}>
              {t('setting.autoupdate.upload_hint')}
            </p>
            <Form.Group widths='equal'>
              <Form.Input
                label={t('setting.autoupdate.upload_file')}
                type='file'
                id='update-file-input'
              />
              <Form.Input
                label={t('setting.autoupdate.upload_version')}
                placeholder={t('setting.autoupdate.upload_version_placeholder')}
                value={uploadVersion}
                onChange={(e, { value }) => setUploadVersion(value)}
              />
              <Form.Dropdown
                label={t('setting.autoupdate.upload_platform')}
                selection
                search
                value={uploadPlatform}
                onChange={(e, { value }) => setUploadPlatform(value)}
                options={[
                  { key: 'auto', value: '', text: t('setting.autoupdate.upload_platform_auto', { defaultValue: '留空自动推断' }) },
                  { key: 'linux-amd64', value: 'linux-amd64', text: 'linux-amd64' },
                  { key: 'linux-arm64', value: 'linux-arm64', text: 'linux-arm64' },
                  { key: 'windows-amd64', value: 'windows-amd64', text: 'windows-amd64' },
                  { key: 'darwin-amd64', value: 'darwin-amd64', text: 'darwin-amd64' },
                  { key: 'darwin-arm64', value: 'darwin-arm64', text: 'darwin-arm64' },
                ]}
              />
            </Form.Group>
            <Form.Button
              color='green'
              type='button'
              loading={uploading}
              disabled={uploading}
              onClick={handleUpload}
            >
              {t('setting.autoupdate.buttons.upload')}
            </Form.Button>
            {uploadResult && (
              <Message
                warning={uploadResult.matches_current || Boolean(uploadResult.warning)}
                success={!uploadResult.matches_current && !uploadResult.warning}
                size='small'
              >
                <Message.Header>{t('setting.autoupdate.messages.upload_success')}</Message.Header>
                <p title={uploadResult.sha256 || undefined}>
                  {t('setting.autoupdate.upload_result', {
                    platform: uploadResult.platform || '-',
                    version: uploadResult.version || '-',
                    hash: uploadResult.short_sha256 || shortHash(uploadResult.sha256),
                    file: uploadResult.file_name || '-',
                    size: formatBytes(uploadResult.size),
                  })}
                </p>
                {uploadResult.matches_current && (
                  <p><strong>{t('setting.autoupdate.messages.upload_matches_current')}</strong></p>
                )}
                {uploadResult.warning && (
                  <p>{t('setting.autoupdate.messages.upload_warning', { warning: uploadResult.warning })}</p>
                )}
              </Message>
            )}
          </Form>
        </Grid.Column>
      </Grid>
    </div>
  );
};

export default AutoUpdateSetting;
